// Deriving a legible text colour from a colour nobody controls.
//
// A deployment's `theme.header` is whatever a tenant admin picked. It is used
// as branding, and branding colours are chosen to look right as a large block
// of fill, not to be read as small text on white. The default is `#2ba9bf`,
// which is 2.79:1 on white: below the WCAG AA floor of 4.5:1 for normal text
// and below the 3:1 floor for large text as well.
//
// That matters here rather than being a general tidy-up, because the string
// set in that colour on the person tab is the PATIENT'S NAME. A clinician
// reading the wrong patient's chart is the failure this whole view exists to
// prevent, so the one line that says who they are looking at has to be
// legible on every tenant's palette, not just on the ones that happen to
// have picked a dark colour.
//
// Extracted from MultipleBirthStatus, which had already needed exactly this
// and had it as a private helper. There is one implementation on purpose:
// two copies drift, and the one that drifts is the one nobody is looking at.

import { darken, getContrastRatio } from '@mui/material/styles';

/**
 * Darken `colour` until it clears a contrast floor against white.
 *
 * Measured against white rather than the actual surface, which is usually a
 * shade darker. That errs toward MORE contrast than required, never less.
 *
 * A pale colour needs several steps. The loop is bounded because `darken`
 * converges on black, so a colour that cannot reach the floor still
 * terminates and still comes back darker than it went in.
 *
 * ONLY PASS AN OPAQUE COLOUR. MUI's `getContrastRatio` does not composite
 * alpha over the surface: it scores `rgba(0, 0, 0, 0.38)` as 21:1, the ratio
 * for solid black, when the pixel a user actually sees is about 2.8:1. So a
 * translucent input exits this loop on the first check and is returned
 * unchanged, having "passed" a test of a colour that is not on screen. If you
 * want a muted tone, pick an opaque one such as `REFRESH_COLOURS.summary`.
 *
 * A colour MUI cannot parse comes straight back out. `getContrastRatio`
 * throws on `undefined`, `null`, `''` and any unrecognised string, and this
 * runs during render of the strip that identifies the patient, so a tenant
 * with an unset or fat-fingered `theme.header` would get a blank chart rather
 * than an off-brand one. Failing soft is the right trade here: the caller
 * falls back to its own ink and the clinician still sees who they are
 * looking at.
 *
 * @param {string} colour  an opaque CSS colour
 * @param {number} [min]   the contrast floor. 4.5 is AA for normal text; use
 *   3 only for large text (18.66px bold or 24px) or a non-text indicator.
 * @returns {string} the same colour, or a darkened one that clears `min`
 */
export const readableOn = (colour, min = 4.5) => {
  let out = colour;
  try {
    for (let i = 0; i < 12 && getContrastRatio(out, '#fff') < min; i += 1) {
      out = darken(out, 0.12);
    }
  } catch {
    return colour;
  }
  return out;
};

/**
 * Black or white ink for text drawn ON a coloured fill, whichever reads better.
 *
 * `readableOn` above answers a different question (a colour used AS text on a light
 * surface) and changes the colour itself. This leaves the fill alone — it is a
 * disease's colour, which means something clinically — and picks the ink instead.
 *
 * Why not MUI's `getContrastText`: its default threshold is 3, so it returns white on
 * Coral `#fc6044` (3.05:1) and Periwinkle (3.06:1), both below AA. Taking the higher
 * of the two ratios clears 4.5:1 for every opaque colour — the worst case is about
 * 4.58:1 — so no threshold is needed at all. Hardcoding white, which the legend drag
 * chip used to do, fails AA on 30 of the 46 palette colours (Lime, Peach, Cream…).
 *
 * The same opaque-input caveat as `readableOn` applies. An unparseable colour (MUI
 * throws on `''`, `undefined` and named colours such as `'black'`) gets white, the ink
 * the callers used before.
 *
 * @param {string} background  an opaque CSS colour
 * @returns {'#000'|'#fff'}
 */
export const inkOn = (background) => {
  try {
    return getContrastRatio(background, '#000') >
      getContrastRatio(background, '#fff')
      ? '#000'
      : '#fff';
  } catch {
    return '#fff';
  }
};
